import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import {
  postParcelVoid,
  getParcelPinPadTransactions,
  registerParcelPinPadVoid,
} from '../../../../actions';
import { postTotalPosVoid } from '../../../../actions/totalpos/Totalpos';
import { PARCEL_STATUS } from '../../../../config/constants';
import SaveButton from '../../../common/button/SaveButton';
import { numberFormatter } from '../../../../utils/number';
import printTotalPosVoid from '../../../../utils/printers/TotalPosVoid';

const VoidButton = ({
  dispatchPostParcelVoid,
  dispatchGetParcelPinPadTransactions,
  dispatchRegisterParcelPinPadVoid,
  dispatchPostTotalPosVoid,
  parcelId,
  status,
  user: { id: userId },
}) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [checkingParcel, setCheckingParcel] = useState(false);
  const [pinPadTransactionList, setPinPadTransactionList] = useState([]);
  const [pinPadProcessing, setPinPadProcessing] = useState({});
  const [pinPadVoidResponses, setPinPadVoidResponses] = useState({});

  const allPinPadTransactionsVoided = pinPadTransactionList.every(
    (transaction) => transaction.voided,
  );

  const refreshPinPadTransactionList = async () => {
    try {
      const refreshed = await dispatchGetParcelPinPadTransactions(parcelId);
      setPinPadTransactionList(refreshed || []);
    } catch ({ message }) {
      toastr.error('Error', message);
    }
  };

  const openConfirmationModal = async () => {
    setCheckingParcel(true);
    try {
      const response = await dispatchGetParcelPinPadTransactions(parcelId);
      setPinPadTransactionList(response || []);
      setPinPadProcessing({});
      setPinPadVoidResponses({});
      setShowConfirmationModal(true);
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      setCheckingParcel(false);
    }
  };

  const closeConfirmationModal = () => setShowConfirmationModal(false);

  const handleVoidPinPadTransaction = async (transaction) => {
    setPinPadProcessing((prev) => ({ ...prev, [transaction.id]: true }));

    const response = await dispatchPostTotalPosVoid({
      financialReference: transaction.financialReference,
      operator: String(userId),
      amount: parseFloat(transaction.amount),
      qrPayment: transaction.paymentMethod === 'QR',
    });

    if (!response) {
      setPinPadProcessing((prev) => {
        const next = { ...prev };
        delete next[transaction.id];
        return next;
      });
      return;
    }

    setPinPadVoidResponses((prev) => ({ ...prev, [transaction.id]: response }));

    try {
      await dispatchRegisterParcelPinPadVoid({
        parcelId,
        originalTransactionId: transaction.id,
        operation: 'VOID',
        approved: Boolean(response.approved),
        responseCode: response.responseCode,
        response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    }

    await refreshPinPadTransactionList();
    setPinPadProcessing((prev) => {
      const next = { ...prev };
      delete next[transaction.id];
      return next;
    });
  };

  const handlePrintPinPadVoid = (transaction) => {
    const response = pinPadVoidResponses[transaction.id];
    if (!response) return;
    printTotalPosVoid(response, transaction.paymentMethod === 'QR');
  };

  const handleParcelVoid = () => {
    if (!allPinPadTransactionsVoided) {
      return;
    }

    closeConfirmationModal();
    dispatchPostParcelVoid({ parcelId });
  };

  if (status !== PARCEL_STATUS.RECEIVED.value) {
    return null;
  }

  return (
    <>
      <Button
        color="danger"
        onClick={openConfirmationModal}
        disabled={checkingParcel}
      >
        <i className={`fa fa-${checkingParcel ? 'spinner fa-spin' : 'ban'}`} />{' '}
        Anulación
      </Button>
      <Modal isOpen={showConfirmationModal}>
        <ModalHeader>Confirmación de Anulación</ModalHeader>
        <ModalBody>
          {pinPadTransactionList.length > 0 ? (
            <div>
              <p>
                Se debe anular el cobro en el POS de cada tarjeta antes de
                confirmar la anulación de la encomienda:
              </p>
              <ul className="list-unstyled">
                {pinPadTransactionList.map((transaction) => {
                  const processing = Boolean(pinPadProcessing[transaction.id]);
                  return (
                    <li key={transaction.id} className="mb-2">
                      {`${transaction.cardApplication || ''} ${
                        transaction.cardNumber || ''
                      } - ${numberFormatter({
                        style: 'currency',
                        value: transaction.amount,
                      })}`}{' '}
                      {transaction.voided ? (
                        <>
                          <span className="text-success">
                            <i className="fa fa-check" /> Anulado
                          </span>{' '}
                          {pinPadVoidResponses[transaction.id] && (
                            <Button
                              size="sm"
                              color="secondary"
                              onClick={() => handlePrintPinPadVoid(transaction)}
                            >
                              <i className="fa fa-print" /> Imprimir
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          size="sm"
                          color="danger"
                          disabled={processing}
                          onClick={() =>
                            handleVoidPinPadTransaction(transaction)
                          }
                        >
                          <i
                            className={`fa fa-${
                              processing ? 'spinner fa-spin' : 'credit-card'
                            }`}
                          />{' '}
                          Anular en POS
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p>¿Está seguro que desea anular esta encomienda?</p>
          )}
        </ModalBody>
        <ModalFooter>
          <SaveButton
            action={handleParcelVoid}
            disabled={!allPinPadTransactionsVoided}
            text="Confirmar"
          />{' '}
          <Button color="secondary" onClick={closeConfirmationModal}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

VoidButton.propTypes = {
  dispatchPostParcelVoid: PropTypes.func.isRequired,
  dispatchGetParcelPinPadTransactions: PropTypes.func.isRequired,
  dispatchRegisterParcelPinPadVoid: PropTypes.func.isRequired,
  dispatchPostTotalPosVoid: PropTypes.func.isRequired,
  parcelId: PropTypes.number.isRequired,
  status: PropTypes.string.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  user: authentication.get('user'),
});

const mapDispatchToProps = {
  dispatchPostParcelVoid: postParcelVoid,
  dispatchGetParcelPinPadTransactions: getParcelPinPadTransactions,
  dispatchRegisterParcelPinPadVoid: registerParcelPinPadVoid,
  dispatchPostTotalPosVoid: postTotalPosVoid,
};

export default connect(mapStateToProps, mapDispatchToProps)(VoidButton);
